import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  KnowledgeChatService, 
  KnowledgeChatMessage, 
  KnowledgeContext, 
  KnowledgeChatStatus,
  KnowledgeChatStatusEvent,
  MessageChunkEvent,
  MessageFinalizedEvent
} from '../services/knowledgeChatService';

interface UseKnowledgeChatOptions {
  knowledgeItemId: string;
  knowledgeContext: KnowledgeContext;
  backgroundChatService?: KnowledgeChatService | null; // Готовый сервис от фонового хука
  cachedHistory?: KnowledgeChatMessage[] | null; // Предзагруженная история
  onMessageSent?: () => void; // Колбэк для уведомления об успешной отправке
}

export const useKnowledgeChat = ({ knowledgeItemId, knowledgeContext, backgroundChatService, cachedHistory, onMessageSent }: UseKnowledgeChatOptions) => {
  const [messages, setMessages] = useState<KnowledgeChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [chatStatus, setChatStatus] = useState<KnowledgeChatStatus>(KnowledgeChatStatus.Idle);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  
  // Состояния для потоковых сообщений
  const [streamingMessages, setStreamingMessages] = useState<Map<string, string>>(new Map());
  
  const chatServiceRef = useRef<KnowledgeChatService | null>(null);

  // Инициализация чата
  useEffect(() => {
    if (!knowledgeItemId) return;

    const initializeChat = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Очищаем сообщения при смене элемента знаний
        setMessages([]);
        setIsTyping(false);
        setChatStatus(KnowledgeChatStatus.Idle);
        setHistoryLoaded(false);
        setIsLoadingHistory(false);
        
        // Сохраняем текущий knowledgeItemId для проверки актуальности запроса
        const currentKnowledgeItemId = knowledgeItemId;
        
        // Функция для настройки обработчиков событий
        const setupEventHandlers = (service: KnowledgeChatService) => {
          service.onMessageReceived((message: KnowledgeChatMessage) => {
            if (currentKnowledgeItemId === knowledgeItemId) {
              // Обрабатываем только пользовательские сообщения - ИИ сообщения идут через поток
              if (!message.isFromAI) {
                setMessages(prevMessages => {
                  // Проверяем, не существует ли уже сообщение с таким ID
                  const existingMessage = prevMessages.find(msg => msg.id === message.id);
                  if (existingMessage) {
                    return prevMessages;
                  }
                  
                  // Добавляем новое сообщение
                  return [...prevMessages, message];
                });
              }
            }
          });

          service.onStatusChanged((statusEvent: KnowledgeChatStatusEvent) => {
            const actualStatus = statusEvent.status !== undefined ? statusEvent.status : (statusEvent as any).Status;
            setChatStatus(actualStatus);
          });

          service.onError((errorMessage: string) => {
            console.error('KnowledgeChat error:', errorMessage);
            setError(errorMessage);
            setIsTyping(false);
            setChatStatus(KnowledgeChatStatus.Idle);
          });

          // Обработчик отключения
          service.onDisconnected(() => {
            setIsConnected(false);
          });

          service.onMessageDeleted((messageId: string) => {
            setMessages(prev => prev.filter(msg => msg.id !== messageId));
          });

          service.onChatCleared((data: { knowledgeItemId: string }) => {
            // Проверяем соответствие knowledgeItemId
            const isMatchingKnowledgeItem = data.knowledgeItemId === knowledgeItemId;
            
            if (isMatchingKnowledgeItem) {
              setMessages([]);
              setIsTyping(false);
              setChatStatus(KnowledgeChatStatus.Idle);
              setHistoryLoaded(false);
            }
          });

          // Обработчик фрагментов сообщений (потоковый режим)
          service.onMessageChunk((chunk: MessageChunkEvent) => {
            // Убираем статус "печатает" при первом chunk
            setIsTyping(false);
            setChatStatus(KnowledgeChatStatus.Idle);
            
            setStreamingMessages(prev => {
              const updated = new Map(prev);
              updated.set(chunk.messageId, chunk.fullContent);
              return updated;
            });
            
            // Обновляем/создаем сообщение в основном списке для потокового отображения
            setMessages(prev => {
              const existingIndex = prev.findIndex(msg => msg.id === chunk.messageId);
              
              if (existingIndex >= 0) {
                // Обновляем существующее сообщение
                const updated = [...prev];
                updated[existingIndex] = { ...updated[existingIndex], content: chunk.fullContent };
                return updated;
              } else {
                // Создаем новое ИИ сообщение для потока
                const aiMessage: KnowledgeChatMessage = {
                  id: chunk.messageId,
                  content: chunk.fullContent,
                  isFromAI: true,
                  timestamp: new Date().toISOString()
                };
                return [...prev, aiMessage];
              }
            });
          });

          // Обработчик финализации сообщения (потоковый режим)
          service.onMessageFinalized((finalized: MessageFinalizedEvent) => {
            // Удаляем из потоковых сообщений
            setStreamingMessages(prev => {
              const updated = new Map(prev);
              updated.delete(finalized.messageId);
              return updated;
            });
            
            // Финализируем сообщение в списке (обновляем существующее)
            setMessages(prev => {
              const existingMessageIndex = prev.findIndex(msg => msg.id === finalized.messageId);
              if (existingMessageIndex >= 0) {
                // Обновляем существующее сообщение финальным контентом
                const updated = [...prev];
                updated[existingMessageIndex] = { ...updated[existingMessageIndex], content: finalized.finalContent };
                return updated;
              }
              // Если сообщения нет - это странно, но не создаем новое
              console.warn('Финализация сообщения, которого нет в списке:', finalized.messageId);
              return prev;
            });
          });

          // Обработчик переподключения - НЕ загружаем историю повторно
          service.onReconnected(() => {
            setIsConnected(true);
            // Не загружаем историю повторно при переподключении, 
            // так как она уже загружена и может содержать новые сообщения
          });
        };
        
        // Используем готовый фоновый сервис или создаем новый
        let chatService: KnowledgeChatService;
        
        if (backgroundChatService) {
          console.log(`[KnowledgeChat] Используем готовый фоновый сервис для документа ${knowledgeItemId}`);
          chatService = backgroundChatService;
          
          // Отключаем предыдущий сервис если он существует И отличается от нового
          if (chatServiceRef.current && chatServiceRef.current !== chatService) {
            console.log(`[KnowledgeChat] Отключаем предыдущий сервис (отличается от фонового)`);
            const oldService = chatServiceRef.current;
            chatServiceRef.current = null;
            oldService.disconnect().catch(console.warn);
          }
          
          chatServiceRef.current = chatService;
          
          // Настраиваем обработчики событий для фонового сервиса
          setupEventHandlers(chatService);
          
          // Проверяем реальное состояние подключения фонового сервиса
          const isActuallyConnected = chatService.connected;
          console.log(`[KnowledgeChat] Состояние подключения фонового сервиса: ${isActuallyConnected}`);
          setIsConnected(isActuallyConnected);
          
          // Если сервис не подключен, пытаемся переподключить
          if (!isActuallyConnected) {
            console.log(`[KnowledgeChat] Фоновый сервис не подключен, выполняем переподключение`);
            try {
              await chatService.connect();
          setIsConnected(true);
              console.log(`[KnowledgeChat] Переподключение фонового сервиса успешно`);
            } catch (reconnectError) {
              console.error(`[KnowledgeChat] Ошибка переподключения фонового сервиса:`, reconnectError);
              setIsConnected(false);
              setError('Не удалось подключиться к чату');
              setIsLoading(false);
              return;
            }
          }
          
          // Используем кешированную историю если она есть, иначе загружаем
          if (cachedHistory && cachedHistory.length >= 0) {
            console.log(`[KnowledgeChat] Используем кешированную историю: ${cachedHistory.length} сообщений`);
            setMessages(cachedHistory);
            setHistoryLoaded(true);
            setIsLoading(false);
          } else {
            console.log(`[KnowledgeChat] Кешированная история отсутствует, загружаем с сервера`);
            // Пытаемся получить уже загруженную историю из фонового сервиса
            try {
              const history = await chatService.getChatHistory(knowledgeItemId);
              if (currentKnowledgeItemId === knowledgeItemId) {
                setMessages(history);
                setHistoryLoaded(true);
                console.log(`[KnowledgeChat] История из фонового сервиса загружена: ${history.length} сообщений`);
              }
            } catch (historyError) {
              console.warn('Ошибка загрузки истории из фонового сервиса:', historyError);
              if (chatServiceRef.current === chatService) {
                setError('Не удалось загрузить историю чата');
              }
            } finally {
              setIsLoading(false);
            }
          }
          
          return; // Выходим, так как инициализация завершена
        }
        
        // Создаем новый сервис если фоновый не передан
        chatService = new KnowledgeChatService();
        
        // Отключаем предыдущий сервис если он существует
        if (chatServiceRef.current) {
          const oldService = chatServiceRef.current;
          chatServiceRef.current = null;
          // Отключаем асинхронно, чтобы не блокировать новое соединение
          oldService.disconnect().catch(console.warn);
        }
        
        chatServiceRef.current = chatService;

        // Настраиваем обработчики событий для нового сервиса
        setupEventHandlers(chatService);

        // Подключаемся к SignalR Hub
        try {
          await chatService.connect();
          setIsConnected(true);
        } catch (connectError) {
          console.error('❌ Ошибка подключения к SignalR Hub:', connectError);
          
          if (chatServiceRef.current === chatService) {
            setIsConnected(false);
            setError('Не удалось подключиться к чату');
          }
          throw connectError;
        }

        // Загружаем историю сообщений
        setIsLoadingHistory(true);
        
        try {
          // Проверяем что соединение все еще активно
          if (chatServiceRef.current !== chatService) {
            setIsLoadingHistory(false);
            return;
          }
          
          const history = await chatService.getChatHistory(knowledgeItemId);
          
          // Проверяем что соединение все еще активно
          if (chatServiceRef.current !== chatService) {
            return;
          }
          
          // Проверяем что запрос все еще актуален
          if (currentKnowledgeItemId !== knowledgeItemId) {
            return;
          }
          
          setMessages(history);
          setHistoryLoaded(true);
          
        } catch (historyError) {
          console.error('❌ Ошибка загрузки истории чата базы знаний:', historyError);
          if (chatServiceRef.current === chatService) {
            setError('Не удалось загрузить историю чата');
          }
        } finally {
          setIsLoadingHistory(false);
        }
        
      } catch (error) {
        console.error('❌ Ошибка инициализации чата базы знаний:', error);
        if (chatServiceRef.current) {
          setError('Не удалось инициализировать чат');
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeChat();

    // Очистка при размонтировании
    return () => {
      if (chatServiceRef.current) {
        chatServiceRef.current.disconnect().catch(console.warn);
        chatServiceRef.current = null;
      }
      // Очищаем состояние при размонтировании
      setMessages([]);
      setIsTyping(false);
      setChatStatus(KnowledgeChatStatus.Idle);
      setIsConnected(false);
      setError(null);
      setHistoryLoaded(false);
    };
  }, [knowledgeItemId, backgroundChatService, cachedHistory]);

  // Функция отправки сообщения
  const sendMessage = useCallback(async (message: string) => {
    const chatService = chatServiceRef.current;
    
    if (!chatService) {
      setError('Сервис чата не инициализирован');
      return;
    }

    // Проверяем подключение и пытаемся переподключиться если нужно
    if (!chatService.connected) {
      console.log('[KnowledgeChat] Соединение потеряно, пытаемся переподключиться...');
      setError('Переподключение к чату...');
      
      try {
        await chatService.connect();
        setIsConnected(true);
        setError(null);
        console.log('[KnowledgeChat] Переподключение успешно');
      } catch (reconnectError) {
        console.error('[KnowledgeChat] Ошибка переподключения:', reconnectError);
        setError('Не удалось подключиться к чату. Попробуйте перезагрузить страницу.');
        setIsConnected(false);
        return;
      }
    }

    try {
      setIsTyping(true);
      setChatStatus(KnowledgeChatStatus.AITyping);
      setError(null); // Очищаем предыдущие ошибки
      
      // Всегда используем потоковый режим для чата базы знаний
      await chatService.sendMessageStream(knowledgeItemId, message, '', knowledgeContext);
      
      // Уведомляем о успешной отправке
      if (onMessageSent) {
        onMessageSent();
      }
    } catch (e) {
      console.error('Ошибка отправки сообщения:', e);
      
      // Проверяем, не является ли ошибка проблемой соединения
      const errorMessage = e instanceof Error ? e.message : String(e);
      if (errorMessage.includes('connection') || errorMessage.includes('Connection') || errorMessage.includes('disconnect')) {
        setError('Потеряно соединение с сервером. Попробуйте снова.');
        setIsConnected(false);
      } else {
        setError('Не удалось отправить сообщение');
      }
      
      setIsTyping(false);
      setChatStatus(KnowledgeChatStatus.Idle);
    }
  }, [knowledgeItemId, knowledgeContext]);

  // Функция удаления сообщения с оптимистичным обновлением
  const deleteMessage = useCallback(async (messageId: string) => {
    const chatService = chatServiceRef.current;
    
    if (!chatService) {
      setError('Сервис чата не инициализирован');
      return;
    }

    // Проверяем подключение
    if (!chatService.connected) {
      setError('Нет подключения к чату. Не удалось удалить сообщение.');
      return;
    }
    
    // Сохраняем сообщение для возможного восстановления
    const messageToDelete = messages.find(msg => msg.id === messageId);
    if (!messageToDelete) return;
    
    // Оптимистично удаляем сообщение сразу
    setMessages(prev => prev.filter(msg => msg.id !== messageId));
    
    try {
      await chatService.deleteMessage(messageId);
    } catch (e) {
      console.error('❌ Ошибка удаления сообщения:', e);
      
      // Проверяем, не является ли ошибка проблемой соединения
      const errorMessage = e instanceof Error ? e.message : String(e);
      if (errorMessage.includes('connection') || errorMessage.includes('Connection') || errorMessage.includes('disconnect')) {
        setError('Потеряно соединение с сервером. Не удалось удалить сообщение.');
        setIsConnected(false);
      } else {
        setError('Не удалось удалить сообщение');
      }
      
      // Восстанавливаем сообщение в случае ошибки
      setMessages(prev => {
        // Проверяем, что сообщение еще не восстановлено
        if (prev.some(msg => msg.id === messageId)) {
          return prev;
        }
        
        // Находим правильное место для вставки сообщения обратно
        const messageIndex = messages.findIndex(msg => msg.id === messageId);
        if (messageIndex === -1) {
          return [...prev, messageToDelete];
        }
        
        // Вставляем сообщение на его первоначальное место
        const newMessages = [...prev];
        newMessages.splice(messageIndex, 0, messageToDelete);
        return newMessages;
      });
    }
  }, [messages]);

  // Функция очистки истории
  const clearChatHistory = useCallback(async () => {
    const chatService = chatServiceRef.current;
    
    if (!chatService) {
      setError('Сервис чата не инициализирован');
      return;
    }

    if (!chatService.connected) {
      setError('Нет подключения к чату. Не удалось очистить историю.');
      return;
    }

    try {
      await chatService.clearChatHistory(knowledgeItemId);
    } catch (e) {
      console.error('Ошибка очистки истории чата:', e);
      
      // Проверяем, не является ли ошибка проблемой соединения
      const errorMessage = e instanceof Error ? e.message : String(e);
      if (errorMessage.includes('connection') || errorMessage.includes('Connection') || errorMessage.includes('disconnect')) {
        setError('Потеряно соединение с сервером. Не удалось очистить историю.');
        setIsConnected(false);
      } else {
        setError('Не удалось очистить историю чата');
      }
    }
  }, [knowledgeItemId]);

  return { 
    messages, 
    isTyping,
    chatStatus,
    isConnected,
    isLoading,
    isLoadingHistory,
    error,
    historyLoaded,
    sendMessage,
    deleteMessage,
    clearChatHistory,
    streamingMessages
  };
}; 